import { Inline, LoadingSpinner, Stack } from '@amboss/design-system';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { config } from '../../config';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import useBottomModal from '../../hooks/useBottomModal';
import useCreditsPaginate from '../../hooks/useCreditsPaginate';
import useLoading from '../../hooks/useLoading';
import { useModal } from '../../hooks/useModal';
import { useSocketEmit } from '../../hooks/useSocketEmit';
import {
  creditsTypeSelector,
  setActiveTab,
  tabSelector,
} from '../../store/credits/creditsSlice';

type CourseClaimModalProps = {
  creditIds: string[];
  surveyUrl: string;
};

const CourseClaimModal = ({ creditIds, surveyUrl }: CourseClaimModalProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const creditType = useAppSelector(creditsTypeSelector);
  const activeTab = useAppSelector(tabSelector);
  const { closeModal } = useModal();
  const { resetBottomModalForm } = useBottomModal();
  const { setLoading } = useLoading();
  const { refreshCredits } = useCreditsPaginate(config.tabs.REVIEWED);
  const { claimCredit } = useSocketEmit();
  const [surveyLoading, setSurveyLoading] = useState(true);

  const iframeUrl = useMemo(() => {
    const params = new URLSearchParams({ survey: surveyUrl });
    return `/alchemer-embed.html?${params.toString()}`;
  }, [surveyUrl]);

  const submitClaim = useCallback(() => {
    if (!creditIds?.length) {
      alert(t('credits.tabs.tab2.claim.error'));
      return;
    }
    const claimCreditIds = [creditIds[0]];

    const courseRating = {
      qa: [],
      note: 'Course evaluation completed in Alchemer.',
    };

    setLoading(true);
    claimCredit({ creditIds: claimCreditIds, type: creditType, rating: courseRating })
      .then(() => {
        closeModal();
        resetBottomModalForm();
        refreshCredits();
        dispatch(setActiveTab(activeTab + 1));
      })
      .catch((e) => {
        const serverMessage =
          e?.result?.error?.message || e?.result?.message || e?.message || '';
        alert(
          serverMessage
            ? `${t('credits.tabs.tab2.claim.error')} (${serverMessage})`
            : t('credits.tabs.tab2.claim.error'),
        );
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    activeTab,
    claimCredit,
    closeModal,
    creditIds,
    creditType,
    dispatch,
    refreshCredits,
    resetBottomModalForm,
    setLoading,
    t,
  ]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (!event.data || event.data.type !== 'alchemer_complete') return;
      submitClaim();
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [submitClaim]);

  return (
    <div
      style={{
        border: '1px solid #d9e0e6',
        borderRadius: 8,
        overflow: 'hidden',
        height: 'calc(92vh - 150px)',
        minHeight: 520,
      }}
    >
      {surveyLoading && (
        <Stack height='100%' vAlignItems='center'>
          <LoadingSpinner screenReaderText={t('general.loadingAccessibilityLabel')} />
        </Stack>
      )}

      <iframe
        title='Course evaluation questionnaire'
        src={iframeUrl}
        style={{ width: '100%', height: '100%', border: 0 }}
        onLoad={() => {
          setSurveyLoading(false);
        }}
      />
    </div>
  );
};

export default CourseClaimModal;
